import Logo from "~/components/Logo"
import {
  accompanyingPrevious,
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  faqH3Style,
  faqTwoCol,
  figCaptionStyle,
  figCaptionStyle2,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { golden, vars } from "~/components/styles/theme.css"
import { Bold, Figure, H2, H3, Image, P, Section } from "~/components/articleComponents"
import ScrollyWolly from "~/components/ScrollyWolly"

const TITLE = "AI Garden Design Tool: Visualise Your Garden Instantly | Rescape AI"
const DESCRIPTION =
  "Rescape AI is an AI garden design tool that transforms a photo of your outdoor space into professional design ideas in seconds. Try the AI garden planner free today."
const URL = "https://rescapeai.com/ai-garden-design-tool"
const IMAGE = "https://rescapeai.com/articles/small-garden-0ed3562e.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2026-04-16",
        dateModified: "2026-04-16",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntity: {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is the best AI garden design tool?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Rescape AI is purpose-built for outdoor spaces and garden design. It uses your actual garden photo as the starting point, producing results that are specific to your space rather than generic illustrations.",
              },
            },
            {
              "@type": "Question",
              name: "Is there a free AI garden planner?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Rescape AI starts from $8.99, making it one of the most accessible AI garden design tools available.",
              },
            },
            {
              "@type": "Question",
              name: "How accurate is AI garden design?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Results are photorealistic and anchored to your existing space, which makes them highly useful for visualisation and decision-making. They are design inspiration tools, not engineering drawings, but for most homeowners, that is exactly what is needed.",
              },
            },
            {
              "@type": "Question",
              name: "Can I use Rescape AI on my phone?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Rescape AI is accessible via any web browser, including on mobile. You can photograph your garden and generate designs without leaving your phone.",
              },
            },
            {
              "@type": "Question",
              name: "Does AI garden design work for small gardens?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Rescape AI works with any size of outdoor space. Small gardens often produce some of the most striking results because the constraints force more creative design choices.",
              },
            },
            {
              "@type": "Question",
              name: "Can I use the AI garden planner for my front garden?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Absolutely. Upload a photo of your front garden and generate ideas for planting, paving, and layout changes.",
              },
            },
          ],
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/landing/contemporary-garden-designed-with-ai.webp)",
          backgroundPositionY: "50%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            AI Garden Design: See Your Garden Transformed in Seconds
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <P>
            Designing a garden used to mean hiring a landscape architect, spending weeks
            on plans, or trying to imagine finished results from a Pinterest board.{" "}
            <Link href="/ai-garden-design">AI garden design</Link> changes that entirely
          </P>
          <P>
            Rescape AI is an AI-powered garden design tool that takes a photograph of your
            existing outdoor space and generates realistic, professional design
            visualisations instantly. You choose the style and instruct creativity (amount
            of change). The AI does the rest.
          </P>
        </section>
        <Section>
          <figure>
            <ScrollyWolly
              before={{
                image: "/ai-garden-design-tool/before-1.webp",
                title: "Before",
                alt: "",
              }}
              after={{
                image: "/ai-garden-design-tool/after-1.webp",
                title: "",
                alt: "Front yard transformed with Rescape AI: Tropical plants garden ideas",
              }}
            />
            <figcaption className={figCaptionStyle2}>
              AI front yard design with Rescape AI: Tropical plant ideas
            </figcaption>
          </figure>
        </Section>
        <Section>
          <H2>What Is AI Garden Design?</H2>
          <P>
            <Link href="/garden-design-guide">AI garden design</Link> uses machine
            learning models trained on landscape imagery to reimagine outdoor spaces based
            on a starting photo and a set of style parameters. The result is a
            photorealistic image showing what your garden could look like, not a generic
            stock photo, but a design built around your actual space.
          </P>
          <P>
            This is fundamentally different from a mood board or a hand-drawn sketch.
            Because the output is anchored to your existing space, the shape of your
            garden, the existing boundaries, the light conditions visible in the photo,
            the results are immediately useful and believable.
          </P>
          <P>
            Rescape AI applies this technology to any outdoor space: back gardens, front
            gardens, side returns, patios, courtyards, balconies, and larger landscape
            projects.
          </P>
        </Section>
        <Image
          src="/ai-garden-design-tool/example-2.webp"
          alt="contemporary front yard design"
        />
        <Section>
          <H2>How the Rescape AI Garden Planner Works</H2>
          <P>The process is straightforward. </P>
          <P>
            Upload a photograph of your outdoor space. This can be taken on any
            smartphone, professional photography is not required. Select a design style
            from the available options, or describe what you are looking for. Rescape AI
            generates multiple design variations, each showing your garden reimagined in
            your chosen direction.
          </P>
          <P>
            You can run the same photo through multiple styles, compare outputs, and
            iterate quickly. Most users generate their first set of results within a few
            minutes of signing up.
          </P>
          {/* <Figure
            imgStyle={{ maxHeight: "360px", objectFit: "cover" }}
            style={{ marginTop: "0.5rem" }}
            src="/articles/small-garden-c866731a.webp"
            caption="Intricate, circular raised planter small garden design | Designed on Rescape AI"
          /> */}
        </Section>
        <Section>
          <H2>AI Garden Design Ideas You Can Generate</H2>
          <P>
            Rescape AI covers a wide range of garden styles and scenarios. Common uses
            include:
          </P>
          <ul className={unorderedList}>
            <li>
              Contemporary and modern garden layouts with clean lines, architectural
              planting, and quality hard landscaping
            </li>
            <li>Low maintenance designs that reduce lawn and simplify planting</li>
            <li>
              Cottage and naturalistic gardens with abundant planting and an informal feel
            </li>
            <li>Summer garden ideas with outdoor entertaining in mind</li>
            <li>Front garden redesigns focused on kerb appeal</li>
            <li>Small garden transformations where space is the primary constraint</li>
          </ul>
          <P>
            The tool is not limited to a fixed style library. Text prompts allow you to
            push toward specific ideas, a particular material, a colour palette, a
            specific feature like a pergola or a water element.
          </P>
        </Section>
        <Section>
          <H2>Why Use an AI Garden Layout Generator?</H2>
          <P>
            The primary reason is confidence. Garden projects are expensive and
            disruptive. Making a significant change, removing a lawn, installing new
            paving, restructuring planting — is hard to undo if the result is not what you
            expected.
          </P>
          <P>
            AI garden design lets you test ideas visually before committing to any
            physical work or spend. It is the fastest way to move from vague inspiration
            to a clear design direction, and it costs a fraction of a professional
            consultation.
          </P>
          <P>
            It is also genuinely useful for briefing contractors. Showing a landscaper a
            Rescape AI output, here is the style, here is the material feel, here is
            roughly what I am after, produces far more accurate quotes and reduces the
            risk of miscommunication.
          </P>
        </Section>
        <Section>
          <H2>AI Garden Design vs Traditional Landscape Design</H2>
          <P>
            Professional <Link href="/ai-landscape-design">landscape design</Link>{" "}
            produces detailed planting plans, specifications, and contractor-ready
            drawings. For large or complex projects, that level of service is worth the
            investment.
          </P>
          <P>
            For most homeowners, though, the priority is not a technical specification, it
            is a clear picture of what their garden could become. That is where AI garden
            design delivers value that traditional methods cannot match on speed or cost.
          </P>
          <P>
            The two approaches are complementary. Many users start with Rescape AI to
            develop their vision, then share the outputs with a landscape designer or
            contractor to take the project forward.
          </P>
        </Section>
        <Section>
          <H2>Getting Started With Rescape AI</H2>
          <P>
            Rescape AI is available online, with no download or installation required.
            Credits start from $8.99 for up-to 125 images, with subscription options from
            $12.49 per month for 250 credits. (Image generation costs either 1 or 2
            credits depending on version.)
          </P>
          <P>
            You can sign up, upload a photo, and generate your first design in under five
            minutes. No design experience, no technical knowledge, no landscaping
            background needed.
          </P>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
            <LinkButton url="/hello/signup">Reimagine with Rescape AI</LinkButton>
          </div>
        </Section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Frequently Asked Questions</h2>
          <div className={`${faqGrid} ${faqTwoCol}`}>
            <div>
              <h3 className={faqH3Style}>What is the best AI garden design tool?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Rescape AI is purpose-built for outdoor spaces and garden design. It uses
                your actual garden photo as the starting point, producing results that are
                specific to your space rather than generic illustrations.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>Is there a free AI garden planner?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Rescape AI starts from $8.99, making it one of the most accessible AI
                garden design tools available.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>How accurate is AI garden design?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Results are photorealistic and anchored to your existing space, which
                makes them highly useful for visualisation and decision-making. They are
                design inspiration tools, not engineering drawings, but for most
                homeowners, that is exactly what is needed.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>Can I use Rescape AI on my phone?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes. Rescape AI is accessible via any web browser, including on mobile.
                You can photograph your garden and generate designs without leaving your
                phone.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                Does AI garden design work for small gardens?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes. Rescape AI works with any size of outdoor space.{" "}
                <Link href="/small-garden-ideas">Small gardens</Link> often produce some
                of the most striking results because the constraints force more creative
                design choices.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                Can I use the AI garden planner for my front garden?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Absolutely. Upload a photo of your front garden and generate ideas for
                planting, paving, and layout changes.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
